import classnames from "classnames";
import { formattedNumber } from "components/utils/portfolio";
import gql from "graphql-tag";
import React, { useContext } from "react";
import { Link, useRouteMatch } from "react-router-dom";
import { Direction, Ordering, createOrdering, reverse } from "utils/ordering";

import OrderingContext, {
  Field,
} from "~/components/Projects/context/OrderingContext";
import EmptyState from "~/components/QAndA/components/EmptyState";

import { ProjectsRadiusTableFragment } from "./ProjectsRadiusTable.generated";

export interface HeaderCellProps {
  name: Field;
  ordering: Ordering<Field>;
  toggleSorting: (name: Field) => void;
  children?: any;
  style?: React.CSSProperties;
  arrowLeft?: boolean;
}

export const HeaderCell = ({
  name,
  ordering,
  toggleSorting,
  children,
  style = { textAlign: "left" },
  arrowLeft = false,
}: HeaderCellProps) => (
  <div style={style}>
    <button
      className={classnames(
        "btn",
        ordering.field && "btn-link",
        ordering.field && "btn-link-hovered"
      )}
      style={{ padding: 0, textAlign: style.textAlign }}
      type="button"
      disabled={!ordering}
      onClick={() => toggleSorting(name)}
    >
      {!arrowLeft ? children : null}
      {ordering.field && (
        <i
          className={classnames(
            "icon",
            "icon--sort-up",
            ordering.field.includes(name) && "active",
            ordering.field.includes(name) &&
              ordering.direction === Direction.ASC &&
              "asc",
            ordering.field.includes(name) &&
              ordering.direction === Direction.DESC &&
              "desc"
          )}
        />
      )}
      {arrowLeft ? children : null}
    </button>
  </div>
);

const getBody = (projects: any, url: string) => (
  <>
    {projects &&
      projects.map((project: any) => (
        <Link
          key={project.id}
          to={`${url}/${project.id}`.replace("//", "/")}
          className="projects-table-row"
        >
          <div className="large">{project.name}</div>
          <div>{project.acquiredPortfolio}</div>
          <div className="large">
            <span>
              {project.location.city}, {project.location.state}
            </span>
          </div>
          <div style={{ width: "10%", textAlign: "right" }}>
            {`${formattedNumber(
              project.withinOneCount,
              0,
              "0"
            )}/${formattedNumber(project.withinTenCount, 0, "0")}`}
          </div>
          <div style={{ width: "10%", textAlign: "right" }}>
            {`${formattedNumber(
              project.withinOneSumCapacityAc,
              0,
              "0"
            )}/${formattedNumber(project.withinTenSumCapacityAc, 0, "0")}`}
          </div>
        </Link>
      ))}
  </>
);

export const PROJECTS_RADIUS_TABLE_FRAGMENT = gql`
  fragment ProjectsRadiusTable on ProjectsCollection {
    projects {
      id
      name
      status
      type
      capacity
      ppaRemainingAvg
      acquiredPortfolio
      location {
        id
        city
        state
      }
      withinOne {
        id
        name
      }
      withinTen {
        id
        name
      }
      withinOneCount
      withinTenCount
      withinOneSumCapacityAc
      withinTenSumCapacityAc
    }
  }
`;

export interface ProjectsRadiusTableProps {
  projects: ProjectsRadiusTableFragment["projects"];
  previousProjects: ProjectsRadiusTableFragment["projects"];
  loading: boolean;
}

const ProjectsRadiusTable = ({
  projects,
  previousProjects,
  loading,
}: ProjectsRadiusTableProps) => {
  const { ordering, setOrdering } = useContext(OrderingContext);
  const { url } = useRouteMatch();

  const toggleSorting = (field: Field) => {
    if (ordering.field === field) {
      setOrdering(reverse(ordering));
    } else {
      setOrdering(createOrdering(field));
    }
  };

  return (
    <>
      <div className="projects-table">
        <div className="projects-table-header">
          <HeaderCell
            name={Field.NAME}
            ordering={ordering}
            toggleSorting={toggleSorting}
          >
            Name
          </HeaderCell>
          <HeaderCell
            name={Field.ACQUIRED_PORTFOLIO}
            ordering={ordering}
            toggleSorting={toggleSorting}
          >
            Portfolio
          </HeaderCell>
          <HeaderCell
            name={Field.LOCATION}
            ordering={ordering}
            toggleSorting={toggleSorting}
          >
            Location
          </HeaderCell>
          <div style={{ width: "16%", textAlign: "right" }}>
            Add'l Proj 1/10 Mi Radius
          </div>
          <div style={{ width: "22%", textAlign: "right" }}>
            Total Sys Size 1/10 Mi Rad (kW AC)
          </div>
        </div>
        {getBody(projects ?? [], url)}
        {loading && getBody(previousProjects ?? [], url)}
      </div>
      {(!projects || !projects.length) && !loading && (
        <EmptyState text="No projects found." />
      )}
    </>
  );
};

export default ProjectsRadiusTable;
